import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {DunningService} from '../../../service/api/dunning.service';
import {BdDunningDto} from '../../../model/dto/api/bd-dunning.dto';
import {BdManualContractDecisionDto} from '../../../model/dto/api/bd-manual-contract-decision.dto';
import {extractError} from '../../../util/error-extraction.util';

/**
 * Erlaubte Feldtypen fuer den generischen Formular-Dialog.
 */
type BdGenericFieldType = 'text' | 'textarea' | 'date' | 'number';

/**
 * Einzelnes Feld in der dynamischen Formular-Konfiguration.
 *
 * Beispiel-JSON:
 * ```json
 * { "name": "kommentar", "label": "Kommentar", "type": "textarea", "required": true }
 * ```
 */
interface BdGenericFormFieldConfig {
    name: string;
    label: string;
    type?: BdGenericFieldType;
    required?: boolean;
    placeholder?: string;
}

/**
 * JSON-Struktur, die in {@code uiComponentConfig} erwartet wird.
 *
 * Beispiel:
 * ```json
 * {
 *   "title": "Spezialaktion",
 *   "fields": [
 *     { "name": "kommentar", "label": "Kommentar", "type": "textarea", "required": true },
 *     { "name": "datum", "label": "Datum", "type": "date" }
 *   ]
 * }
 * ```
 */
interface BdGenericFormConfig {
    title?: string;
    fields?: BdGenericFormFieldConfig[];
}

/**
 * Laufzeit-Status fuer ein gerendertes Feld (Wert + Validierungsfehler).
 */
interface BdGenericFormFieldState {
    config: BdGenericFormFieldConfig;
    value: string;
}

/**
 * AI:
 * Status: 'generated'
 * Type: Component
 * SubType: Exit-UI / Generic-Form-Dialog
 * Reason: Dynamischer Fallback-Dialog fuer beliebige Pending-Actions, deren UI
 *         per {@code uiComponentConfig}-JSON beschrieben wird. Faellt auf eine
 *         reine Begruendungs-Textarea zurueck, wenn keine Konfiguration vorliegt.
 *         Bei Bestaetigung wird {@link DunningService.decideManualContractDecision} mit
 *         {@code action='CONTINUE'} und einem JSON-stringifizierten Comment
 *         (alle Feldwerte) aufgerufen.
 *
 *         Mapped to {@link BdDunningExitUiComponent.GENERIC_FORM}.
 *
 * @ticket TM-1000001782
 */
@Component({
    selector: 'mvs-bd-generic-form-dialog',
    templateUrl: './bd-generic-form-dialog.component.html',
    styleUrls: ['./bd-generic-form-dialog.component.scss'],
    standalone: false
})
export class BdGenericFormDialogComponent implements OnInit {

    // ------------------------------------------------------------------ //
    // Inputs
    // ------------------------------------------------------------------ //

    @Input() dunning: BdDunningDto | null = null;
    @Input() uiComponentConfig: string | null = null;

    // U17: Vom Drawer (ngComponentOutletInputs) durchgereichte Felder. Nicht
    // direkt verwendet, aber als @Input() deklariert, damit die Drawer-Bindung
    // kein NG0303 auslöst.
    @Input() dunningId: number | null = null;
    @Input() contracts: unknown[] | null = null;
    @Input() handOvers: unknown[] | null = null;
    @Input() activePause: unknown | null = null;
    @Input() breachedPlan: unknown | null = null;
    @Input() currentUserId: number | null = null;

    // ------------------------------------------------------------------ //
    // Outputs
    // ------------------------------------------------------------------ //

    @Output() actionCompleted: EventEmitter<void> = new EventEmitter<void>();
    @Output() actionCancelled: EventEmitter<void> = new EventEmitter<void>();

    // ------------------------------------------------------------------ //
    // State
    // ------------------------------------------------------------------ //

    initialized: boolean = false;
    visible: boolean = true;
    submitting: boolean = false;
    errorMessage: string | null = null;

    /** Aus uiComponentConfig.title gelesen oder Fallback-Titel. */
    dialogTitle: string = 'Aktion erforderlich';

    /** Anzeige-Felder fuer den Mahnungs-Header. */
    customerName: string = '—';
    dunningTypeName: string = '—';
    activeLevelLabel: string = '—';

    /** Gerendert aus uiComponentConfig.fields oder Fallback (eine einzige Begruendungs-Textarea). */
    fieldStates: BdGenericFormFieldState[] = [];

    /** Indicator, ob es sich um den Fallback-Modus (keine Config) handelt. */
    isFallback: boolean = false;

    constructor(private readonly dunningService: DunningService) {
    }

    // ------------------------------------------------------------------ //
    // Lifecycle
    // ------------------------------------------------------------------ //

    ngOnInit(): void {
        this.initComponent();
    }

    private initComponent(): void {
        this.customerName = this.buildCustomerName(this.dunning);
        this.dunningTypeName = this.readString(this.dunning, ['dunningTypeName', 'typeName']) ?? '—';
        this.activeLevelLabel = this.readString(this.dunning, ['activeLevelShortName', 'activeLevelName']) ?? '—';

        const parsed = this.parseConfig(this.uiComponentConfig);
        this.dialogTitle = parsed?.title?.trim() || 'Aktion erforderlich';
        const configuredFields = (parsed?.fields ?? []).filter(f => !!f && !!f.name && !!f.label);

        if (configuredFields.length > 0) {
            this.fieldStates = configuredFields.map(config => ({
                config: {
                    name: config.name,
                    label: config.label,
                    type: config.type ?? 'text',
                    required: config.required === true,
                    placeholder: config.placeholder
                },
                value: ''
            }));
            this.isFallback = false;
        } else {
            this.fieldStates = [{
                config: {
                    name: 'comment',
                    label: 'Begründung',
                    type: 'textarea',
                    required: true,
                    placeholder: 'Bitte begründen…'
                },
                value: ''
            }];
            this.isFallback = true;
        }

        this.initialized = true;
    }

    // ------------------------------------------------------------------ //
    // Actions
    // ------------------------------------------------------------------ //

    confirm(): void {
        const missing = this.fieldStates
            .filter(state => state.config.required === true)
            .filter(state => (state.value?.trim() ?? '').length === 0);
        if (missing.length > 0) {
            this.errorMessage = `Bitte alle Pflichtfelder ausfüllen (${missing.map(s => s.config.label).join(', ')}).`;
            return;
        }

        const dunningId = this.resolveDunningId();
        if (dunningId == null) {
            this.errorMessage = 'Keine Mahnungs-ID verfügbar.';
            return;
        }

        const payload: Record<string, string> = {};
        for (const state of this.fieldStates) {
            payload[state.config.name] = state.value?.trim() ?? '';
        }

        const decision: BdManualContractDecisionDto = {
            action: 'CONTINUE',
            comment: JSON.stringify(payload)
        };

        this.errorMessage = null;
        this.submitting = true;

        this.dunningService.decideManualContractDecision(dunningId, decision).subscribe({
            next: () => {
                this.submitting = false;
                this.closeDialog();
                this.actionCompleted.emit();
            },
            error: (err: unknown) => {
                this.submitting = false;
                this.errorMessage = extractError(err);
            }
        });
    }

    onCancel(): void {
        this.closeDialog();
        this.actionCancelled.emit();
    }

    onVisibleChange(visible: boolean): void {
        if (!visible) {
            this.actionCancelled.emit();
        }
    }

    // ------------------------------------------------------------------ //
    // Template helpers
    // ------------------------------------------------------------------ //

    trackByName(_index: number, state: BdGenericFormFieldState): string {
        return state.config.name;
    }

    // ------------------------------------------------------------------ //
    // Helpers
    // ------------------------------------------------------------------ //

    private closeDialog(): void {
        this.visible = false;
    }

    private parseConfig(raw: string | null): BdGenericFormConfig | null {
        if (!raw) {
            return null;
        }
        try {
            const parsed = JSON.parse(raw) as unknown;
            if (parsed && typeof parsed === 'object') {
                return parsed as BdGenericFormConfig;
            }
            return null;
        } catch {
            return null;
        }
    }

    private resolveDunningId(): number | null {
        if (!this.dunning) {
            return null;
        }
        const id = (this.dunning.id ?? this.dunning['dunningId']) as unknown;
        return typeof id === 'number' && id > 0 ? id : null;
    }

    private buildCustomerName(dunning: BdDunningDto | null): string {
        if (!dunning) {
            return '—';
        }
        const first = (dunning.personFirstName ?? dunning.firstName ?? '') as string;
        const last = (dunning.personLastName ?? dunning.lastName ?? '') as string;
        const combined = `${first} ${last}`.trim();
        return combined.length > 0 ? combined : '—';
    }

    private readString(source: BdDunningDto | null, keys: string[]): string | null {
        if (!source) {
            return null;
        }
        for (const key of keys) {
            const value = source[key];
            if (typeof value === 'string' && value.length > 0) {
                return value;
            }
        }
        return null;
    }
}
